import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import Head from 'next/head';
import Script from 'next/script'; //
import { useRouter } from 'next/router';

import { getCurrentHub } from '@sentry/nextjs';
import { TransitioningProvider } from '@/hooks/useTransition';

import ProgressBar from '@/components/Layout/ProgressBar';
import GlobalContainer from '@/components/Layout/GlobalContainer';
import Banner from '@/components/Layout/Banner';
import ErrorPage from '@/components/Layout/ErrorPage';

import './global.css';
import 'swiper/swiper.scss';
import 'swiper/components/pagination/pagination.min.css';
import 'swiper/components/navigation/navigation.min.css';
import 'react-image-gallery/styles/css/image-gallery.css';
import './styles.scss';
import useScriptsToRoute from '@/hooks/useScriptsToRoute';
import { sendPageViewEventToFacebookAPI } from '@/services/fbConvetionAPI';
import { useMarfeelPageTracking } from '@/hooks/useMarfeelPageTracking';

export default function App({ Component, pageProps }) {
  if (pageProps.newsPreview) {
    return (
      <>
        <Head>
          <meta name="viewport" content="width=device-width, initial-scale=1.0, shrink-to-fit=no, user-scalable=yes, maximum-scale=5.0, minimum-scale=0.86" />
          <meta className="versionExt" content="2" />
        </Head>
        <Component {...pageProps} />
      </>
    );
  }

  const transaction = getCurrentHub().getScope()?.getTransaction();
  const appSpan = transaction && transaction.startChild({ op: 'app' });

  const router = useRouter();
  const currentPath = router.pathname;
  const cleanPath = currentPath.split('?')[0].replace(/\/$/, '');

  const { useGpt, useMarfeel, useComscore, useCnn } = useScriptsToRoute(cleanPath);

  // tracking de page-view do Marfeel: 1ª req é auto-rastreada pelo SDK; navegação SPA
  // dispara trackNewPage 1× por rota. Detalhes e logs observe-only vivem no próprio hook.
  useMarfeelPageTracking(useMarfeel);

  // `eventId` estável por navegação: gerado no useEffect (client-only) para não quebrar
  // o SSR de `/404`, `/500`, `/live` no runtime Node 16 (que não tem `crypto` global).
  // Regera a cada mudança de rota; re-renders na mesma rota mantêm o mesmo id, então
  // retries e re-executions do effect deduplicam na Meta (via Events Manager).
  const [pageViewEventId, setPageViewEventId] = useState<string>('');

  useEffect(() => {
    setPageViewEventId(crypto.randomUUID());
  }, [router.asPath]);

  useEffect(() => {
    if (!pageViewEventId) return;
    sendPageViewEventToFacebookAPI({ eventSourceUrl: window.location.href, eventId: pageViewEventId });
  }, [pageViewEventId]);

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, shrink-to-fit=no, user-scalable=yes" />
      </Head>
      <TransitioningProvider>
        <ProgressBar />
        {/* inicio - gpt js */}
        {useGpt && <Script strategy="afterInteractive" async src="https://securepubads.g.doubleclick.net/tag/js/gpt.js" />}
        {/* fim - gpt js */}

        {/* inicio - comscore script */}
        {useComscore && (
          <Script
            dangerouslySetInnerHTML={{
              __html: `var _comscore = _comscore || [];
              _comscore.push({
                c1: "2",
                c2: "20572948",
                options: {
                  enableFirstPartyCookie: true,
                  bypassUserConsentRequirementFor1PCookie: true
                }
              });
              (function() {
                var s = document.createElement("script"), el = document.getElementsByTagName("script")[0];
                s.async = true;
                s.src = "https://sb.scorecardresearch.com/cs/20572948/beacon.js";
                el.parentNode.insertBefore(s, el);
              })();`,
            }}
          />
        )}
        {/* fim - comscore script */}

        {/* inicio - comscore noscript */}
        {useComscore && (
          <noscript>
            <img src="https://sb.scorecardresearch.com/p?c1=2&amp;c2=20572948&amp;cv=cv=4.4.0&amp;cj=1" alt="" />
          </noscript>
        )}
        {/* fim - comscore noscript */}

        {/* inicio - CNN script */}
        {useCnn && <Script id="cnn-partners-sdk-jg" strategy="afterInteractive" async src="https://www.cnnbrasil.com.br/partners_sdk/?enable=badge" />}
        {/* fim - CNN script */}

        <ErrorBoundary
          fallback={
            <>
              <Head>
                <title>Canaltech | Ooops. Algo deu errado</title>
              </Head>
              <GlobalContainer whiteBg center>
                <Banner />
                <ErrorPage isGeneric statusCode="Ooops! Algo deu errado. " />
              </GlobalContainer>
            </>
          }>
          <Component {...pageProps} />
        </ErrorBoundary>
      </TransitioningProvider>
      {appSpan && appSpan.finish()}
    </>
  );
}
